import { ColumnDef } from '@tanstack/react-table';
import { formatUtils } from '@/lib/utils';
import { t } from 'i18next';
import { Hash } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import {
  DataTableFilter,
  RowDataWithActions,
} from '@/components/ui/data-table';
import { DataTableColumnHeader } from '@/components/ui/data-table/data-table-column-header';
import { StatusIconWithText } from '@/components/ui/status-icon-with-text';
import { flowRunUtils } from '@/features/flow-runs/lib/flow-run-utils';
import { AdminExecution, FlowRunStatus } from '@activepieces/shared';

import { CellWithCopyTooltip } from './admin-table-cells';

export const useAdminExecutionsTable = () => {
  const navigate = useNavigate();

  const openExecution = (execution: AdminExecution, newWindow: boolean) => {
    const url = `/internal-admin/executions/${execution.tenantId}/${execution.id}`;
    if (newWindow) {
      window.open(url, '_blank');
    } else {
      navigate(url);
    }
  };

  const columns: ColumnDef<RowDataWithActions<AdminExecution>>[] = [
    {
      accessorKey: 'flowDisplayName',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Flow name')} />
      ),
      cell: ({ row }) => (
        <CellWithCopyTooltip
          displayValue={row.original.flowDisplayName ?? 'N/A'}
          tooltipValue={row.original.id}
        />
      ),
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Status')} />
      ),
      cell: ({ row }) => {
        const status = row.original.status as FlowRunStatus;
        const { variant, Icon } = flowRunUtils.getStatusIcon(status);
        return (
          <StatusIconWithText
            icon={Icon}
            text={formatUtils.convertEnumToHumanReadable(status)}
            variant={variant}
          />
        );
      },
    },
    {
      accessorKey: 'startTime',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Start Time')} />
      ),
      cell: ({ row }) =>
        row.original.startTime
          ? formatUtils.formatDate(new Date(row.original.startTime))
          : '-',
    },
    {
      accessorKey: 'externalId',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('User ID')} />
      ),
      cell: ({ row }) => row.original.externalId ?? 'N/A',
    },
    {
      accessorKey: 'externalCustomerId',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Client ID')} />
      ),
      cell: ({ row }) => row.original.externalCustomerId ?? 'N/A',
    },
    {
      accessorKey: 'flowId',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Flow ID')} />
      ),
      cell: ({ row }) => (
        <CellWithCopyTooltip
          displayValue={row.original.flowId}
          tooltipValue={row.original.flowId}
          truncated
        />
      ),
    },
  ];

  const filters: DataTableFilter<keyof AdminExecution>[] = [
    {
      type: 'input',
      title: t('Search'),
      accessorKey: 'flowDisplayName',
      options: [],
    },
    {
      type: 'input',
      title: t('User ID'),
      accessorKey: 'externalId',
      icon: Hash,
      options: [],
    },
    {
      type: 'input',
      title: t('Client ID'),
      accessorKey: 'externalCustomerId',
      icon: Hash,
      options: [],
    },
    {
      type: 'select',
      title: t('Status'),
      accessorKey: 'status',
      options: Object.values(FlowRunStatus)
        .filter((status) => status !== FlowRunStatus.STOPPED)
        .map((status) => ({
          label: formatUtils.convertEnumToHumanReadable(status),
          value: status,
          icon: flowRunUtils.getStatusIcon(status).Icon,
        })),
    },
    {
      type: 'date',
      title: t('Start Time'),
      accessorKey: 'startTime',
      options: [],
    },
  ];

  return { columns, filters, openExecution };
};
